import { useCallback, useMemo, useRef, useState } from 'react';
import { api, isAuthPending } from '../lib/api';
import { clearSwrCacheByPrefix } from '../lib/swrCache';
import { toast } from '../lib/dialogs';
import type { Venue } from '../types';
import NeighborhoodSelect from './NeighborhoodSelect';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Only the venue fields this editor reads/writes. Keeps map call sites terse. */
export type NeighborhoodEditableVenue = Pick<
  Venue,
  'rowKey' | 'name' | 'partitionKey' | 'manualNeighborhoodOverride'
>;

interface Props {
  venue: NeighborhoodEditableVenue;
  /** Known neighborhood names for the dropdown. The current value is always added. */
  options: string[];
  /** Fired after a successful save so callers can re-render from authoritative state. */
  onChanged?: (rowKey: string, neighborhood: string, manualOverride: boolean) => void;
  /** Optional id for the visible label association. */
  id?: string;
  /** Extra class on the wrapper. */
  className?: string;
  /**
   * Hide the "Manual" badge text and shorten labels — used inside compact map
   * popups where horizontal space is scarce.
   */
  compact?: boolean;
}

/**
 * Admin-only inline neighborhood editor.
 *
 * A venue's neighborhood IS its Table Storage `partitionKey`, so "changing the
 * neighborhood" is a partition move on the API side: `PATCH /api/mgmt/venues/{id}`
 * with `{ partitionKey }`. That endpoint consolidates duplicate rows, stamps
 * `manualNeighborhoodOverride` so the bulk reassign sweep won't undo the
 * choice, and returns the authoritative saved row.
 *
 * The editor deliberately treats the PATCH response as the only source of
 * truth: a failed request reverts to the last value the server actually
 * confirmed (tracked in refs so a stale parent prop can't resurrect an
 * unconfirmed value), and the venues SWR cache is invalidated so other views
 * refetch instead of showing the old partition.
 */
export default function VenueNeighborhoodEditor({
  venue,
  options,
  onChanged,
  id,
  className,
  compact,
}: Props) {
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [neighborhood, setNeighborhood] = useState(venue.partitionKey);
  const [manualOverride, setManualOverride] = useState(!!venue.manualNeighborhoodOverride);

  // Last server-confirmed values. Reverting to these (rather than to props)
  // keeps the UI honest when a save fails before the parent re-renders.
  const committedNeighborhood = useRef(venue.partitionKey);
  const committedManual = useRef(!!venue.manualNeighborhoodOverride);

  const selectId = id ?? `mh-hood-${venue.rowKey}`;

  // Always include the current value: a just-typed name may not have
  // propagated back into `options` yet.
  const mergedOptions = useMemo(() => {
    const set = new Set(options.filter(Boolean));
    const current = neighborhood.trim();
    if (current) set.add(current);
    return Array.from(set);
  }, [options, neighborhood]);

  const save = useCallback(
    async (
      body: { partitionKey?: string; manualNeighborhoodOverride?: boolean },
      nextNeighborhood: string,
      nextManual: boolean,
    ) => {
      setSaveState('saving');
      try {
        await api.patch(`/api/mgmt/venues/${encodeURIComponent(venue.rowKey)}`, body);
        committedNeighborhood.current = nextNeighborhood;
        committedManual.current = nextManual;
        setNeighborhood(nextNeighborhood);
        setManualOverride(nextManual);
        setSaveState('saved');
        // The venue moved partitions — every other view's cached copy is stale.
        clearSwrCacheByPrefix('venues:');
        onChanged?.(venue.rowKey, nextNeighborhood, nextManual);
      } catch (err) {
        // An expired session is already surfaced globally by the auth-required
        // event; don't also flash a red row in the popup.
        if (isAuthPending(err)) {
          setNeighborhood(committedNeighborhood.current);
          setManualOverride(committedManual.current);
          setSaveState('idle');
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setNeighborhood(committedNeighborhood.current);
        setManualOverride(committedManual.current);
        setSaveState('error');
        toast.error(`Could not move "${venue.name}": ${msg}`);
      }
    },
    [onChanged, venue.name, venue.rowKey],
  );

  const handleNeighborhoodChange = (next: string) => {
    // A free-text "add neighbourhood" entry can come back with different
    // capitalisation of the value already saved — that's a no-op, not a new
    // manual pin, so "Reset to auto" stays available.
    if (next === committedNeighborhood.current) return;
    // Picking a different neighbourhood is an explicit admin decision; the API
    // stamps `manualNeighborhoodOverride` too, and we mirror it optimistically.
    void save({ partitionKey: next }, next, true);
  };

  const handleResetToAuto = () => {
    void save({ manualNeighborhoodOverride: false }, committedNeighborhood.current, false);
  };

  return (
    <div
      className={['mh-hood-editor', compact ? 'mh-hood-editor--compact' : '', className ?? ''].filter(Boolean).join(' ')}
      // Leaflet popups are a fixed white surface in every app theme, so pin
      // Bootstrap's variables to the light theme for this subtree — otherwise
      // dark-mode users get a dark <select> (and an invisible chevron) on white.
      data-bs-theme="light"
    >
      <label className="mh-hood-editor__label" htmlFor={selectId}>
        {compact ? 'Area' : 'Neighborhood'}
        {manualOverride && (
          <span
            className="badge bg-info-subtle text-info-emphasis mh-hood-editor__badge"
            title="An admin pinned this neighborhood. The bulk Reassign neighborhoods button skips this venue."
          >
            Manual
          </span>
        )}
      </label>
      <NeighborhoodSelect
        id={selectId}
        className="form-select form-select-sm"
        value={neighborhood}
        options={mergedOptions}
        disabled={saveState === 'saving'}
        ariaLabel={`Neighborhood for ${venue.name}`}
        onChange={handleNeighborhoodChange}
      />
      {manualOverride && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 mh-hood-editor__reset"
          disabled={saveState === 'saving'}
          onClick={handleResetToAuto}
          title="Clear the manual override so the bulk Reassign neighborhoods sweep can update this venue from its coordinates"
        >
          ↻ Reset to auto
        </button>
      )}
      {saveState === 'saving' && <span className="mh-inline-status mh-inline-status--saving" role="status"> Saving…</span>}
      {saveState === 'saved' && <span className="mh-inline-status mh-inline-status--saved" role="status"> ✓ Saved</span>}
      {saveState === 'error' && <span className="mh-inline-status mh-inline-status--error" role="status"> ✗ Save failed</span>}
    </div>
  );
}
